import { Injectable, signal } from '@angular/core';
import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';

type CacheKey = string;

interface ICacheEntry {
    readonly storedAt: number;
    readonly expiresAt: number;
    readonly response: HttpResponse<unknown>;
}

interface IInflightEntry {
    readonly startedAt: number;
    readonly subject: ReplaySubject<HttpResponse<unknown>>;
}

@Injectable({ providedIn: 'root' })
export class HttpCacheService {
    readonly size = signal(0);

    readonly #maxEntries = 500;
    readonly #defaultTtlMs = 30_000;

    readonly #cache = new Map<CacheKey, ICacheEntry>();
    readonly #inflight = new Map<CacheKey, IInflightEntry>();

    readonly #cleanupIntervalMs = 10_000;
    readonly #cleanupTimer: number;

    constructor() {
        this.#cleanupTimer = window.setInterval(
            () => this.evictExpired(),
            this.#cleanupIntervalMs,
        );
    }

    dispose() {
        window.clearInterval(this.#cleanupTimer);

        for (const [, entry] of this.#inflight) {
            entry.subject.complete();
        }
        this.#inflight.clear();
        this.#cache.clear();
        this.size.set(0);
    }

    defaultTtlMs(): number {
        return this.#defaultTtlMs;
    }

    buildKey(req: HttpRequest<unknown>, salt?: string): CacheKey {
        const lang = req.headers.get('Accept-Language') ?? '';
        const tenant = req.headers.get('X-Tenant') ?? '';
        const base = `${req.method} ${req.urlWithParams} | lang=${lang} | tenant=${tenant}`;
        return salt ? `${base} | salt=${salt}` : base;
    }

    get(key: CacheKey): HttpResponse<unknown> | null {
        const entry = this.#cache.get(key);
        if (!entry) return null;

        const now = Date.now();
        if (entry.expiresAt <= now) {
            this.#cache.delete(key);
            this.size.set(this.#cache.size);
            return null;
        }

        return entry.response;
    }

    set(key: CacheKey, response: HttpResponse<unknown>, ttlMs?: number): void {
        const now = Date.now();
        const ttl = ttlMs ?? this.#defaultTtlMs;
        if (ttl <= 0) return;

        if (this.#cache.size >= this.#maxEntries) {
            this.evictOldest(Math.ceil(this.#maxEntries * 0.1));
        }

        this.#cache.set(key, {
            storedAt: now,
            expiresAt: now + ttl,
            response,
        });

        this.size.set(this.#cache.size);
    }

    delete(key: CacheKey): void {
        this.#cache.delete(key);
        this.size.set(this.#cache.size);
    }

    clear(): void {
        this.#cache.clear();
        this.size.set(0);
    }

    beginInflight(key: string): {
        isNew: boolean;
        observable: Observable<HttpResponse<unknown>>;
        subject: ReplaySubject<HttpResponse<unknown>>;
    } {
        const existing = this.#inflight.get(key);
        if (existing) {
            return {
                isNew: false,
                observable: existing.subject.asObservable(),
                subject: existing.subject,
            };
        }

        const subject = new ReplaySubject<HttpResponse<unknown>>(1);
        this.#inflight.set(key, { startedAt: Date.now(), subject });

        return { isNew: true, observable: subject.asObservable(), subject };
    }

    endInflight(key: CacheKey): void {
        this.#inflight.delete(key);
    }

    evictExpired(): void {
        const now = Date.now();
        let changed = false;

        for (const [key, entry] of this.#cache) {
            if (entry.expiresAt <= now) {
                this.#cache.delete(key);
                changed = true;
            }
        }

        if (changed) this.size.set(this.#cache.size);
    }

    evictOldest(count: number): void {
        if (count <= 0) return;

        const entries = Array.from(this.#cache.entries()).sort(
            (a, b) => a[1].storedAt - b[1].storedAt,
        );

        for (let i = 0; i < Math.min(count, entries.length); i++) {
            this.#cache.delete(entries[i]?.[0]);
        }
        this.size.set(this.#cache.size);
    }
}
